import {
  BeneficiaryModel,
  DonationBeneficiaryModel,
  DonationPricingPageInputsModel,
} from "../../../shared/models";
import {
  AuthService,
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  InfoIcon,
  Input,
  IOption,
  CreatableSelect as SelectCreateable,
} from "@mindmemobile/mm-shared-global";
import { inject, observer } from "mobx-react";
import React from "react";
import { Subject } from "rxjs";
import { DonationStore } from "../../../shared/store"; 
import { DescriptionCell, LabelCell } from "../../../shared/styles";

type IProps = {
  donationStore?: DonationStore;
};

@inject("donationStore")
@observer
class DonationAdvanceSection extends React.Component<IProps> {
  public newBeneficiarySubject = new Subject<BeneficiaryModel>();

  get donationStore() {
    return this.props.donationStore;
  }

  updateState(
    key: keyof DonationPricingPageInputsModel,
    value: string | boolean
  ) {
    if (!this.donationStore) return;
    this.props.donationStore?.updateScrollToField(key);
    switch (key) {
      case "canSelectOneBeneficiary":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            canSelectOneBeneficiary: value as boolean,
          })
        );
        break;
      case "canSelectMultipleBeneficiaries":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            canSelectMultipleBeneficiaries: value as boolean,
          })
        );
        break;
      case "allowComments":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            allowComments: value as boolean,
          })
        );
        break;
      case "offerInMemoryOf":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            offerInMemoryOf: value as boolean,
          })
        );
        break;
      case "annualIncrease":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            annualIncrease: value as boolean,
          })
        );
        break;
      case "coverProcessingCost":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            coverProcessingCost: value as boolean,
          })
        );
        break;
      case "allowAnonymousDonation":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            allowAnonymousDonation: value as boolean,
          })
        );
        break;
      case "setLimitOnCustomDonation":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            setLimitOnCustomDonation: value as boolean,
          })
        );
        break;
      case "minimumDonationAmount":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            minimumDonationAmount: value as string,
          })
        );
        break;
      case "maximumDonationAmount":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            maximumDonationAmount: value as string,
          })
        );
        break;
      case "coverProcessingCostDescription":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            coverProcessingCostDescription: value as string,
          })
        );
        break;
      case "annualIncreaseDescription":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            annualIncreaseDescription: value as string,
          })
        );
        break;
    }
  }

  checkBoxChange(
    key: keyof DonationPricingPageInputsModel,
    value: boolean,
    label: string
  ) {
    return (
      <Input
        type="checkbox"
        value={value}
        onChange={(e) => this.updateState(key, e.target.checked)}
        label={label}
      />
    );
  }

  textInput(
    type: string,
    key: keyof DonationPricingPageInputsModel,
    value: string,
    placeHolder: string,
    className: string
  ) {
    return (
      <Input
        type={type as "text" | "textarea"}
        value={value}
        error={this.donationStore?.pricingPageData.hasError(key)}
        className={className}
        onChange={(e) => this.updateState(key, e.target.value)}
        placeholder={placeHolder}
      />
    );
  }

  get options(): IOption[] {
    return (
      this.donationStore?.beneficiaryService?.beneficiaries.map((b) => {
        return {
          label: b.name,
          value: b.id,
        } as IOption;
      }) || []
    );
  }

  updateSelectedBeneficiary(newBeneficiaries: IOption[]) {
    if (!this.donationStore?.pricingPageData) return;
    this.props.donationStore?.updateScrollToField("beneficiaries");

    let hasNew = false;
    if (newBeneficiaries.length > 0) {
      const lastValue = newBeneficiaries[newBeneficiaries.length - 1];
      if (lastValue.__isNew__) {
        hasNew = true;
        this.donationStore.beneficiaryService.createBeneficiary(
          AuthService.authData.accountId,
          lastValue.label,
          this.newBeneficiarySubject
        );

        this.newBeneficiarySubject.subscribe({
          next: (newBeneficiary) => {
            if (newBeneficiary) {
              if (!this.donationStore) return;

              lastValue.value = newBeneficiary.id;

              this.props.donationStore?.updatePricingPageData(
                new DonationPricingPageInputsModel({
                  ...this.donationStore.pricingPageData,
                  beneficiaries: newBeneficiaries.map((b) => {
                    return new DonationBeneficiaryModel({
                      id: b.value,
                      name: b.label,
                    });
                  }),
                })
              );
            }
          },
        });
      }
    }

    if (!hasNew) {
      this.props.donationStore?.updatePricingPageData(
        new DonationPricingPageInputsModel({
          ...this.donationStore.pricingPageData,
          beneficiaries: newBeneficiaries.map((b) => {
            return new DonationBeneficiaryModel({
              id: b.value,
              name: b.label,
            });
          }),
        })
      );
    }
  }

  get selectedBeneficiaries() {
    return (
      this.props.donationStore?.pricingPageData?.beneficiaries?.map((b) => {
        return {
          label: b.name,
          value: b.id,
        } as IOption;
      }) || []
    );
  }

  limitFlex(input: React.ReactNode, label: string) {
    return (
      <div>
        <FlexContainer spacing={15} fxLayout="row" className="dollar-input">
          <div>{input}</div>
        </FlexContainer>

        <CustomLabel>{label}</CustomLabel>
      </div>
    );
  }

  get customLimits() {
    if (!this.donationStore?.pricingPageData.setLimitOnCustomDonation)
      return null;
    return (
      <div>
        <FlexContainer spacing={20}>
          {this.limitFlex(
            <>
              {this.textInput(
                "text",
                "minimumDonationAmount",
                this.donationStore?.pricingPageData?.minimumDonationAmount ||
                  "",
                "",
                "styled-input amount-range"
              )}
            </>,
            "Minimum"
          )}
          {this.limitFlex(
            <>
              {this.textInput(
                "text",
                "maximumDonationAmount",
                this.donationStore?.pricingPageData?.maximumDonationAmount ||
                  "",
                "",
                "styled-input amount-range"
              )}
            </>,
            "Maximum"
          )}
        </FlexContainer>
      </div>
    );
  }

  get coverFee() {
    return (
      <FlexContainer className="label-description-row">
        <LabelCell>
          <CustomLabel lineHeight="40px" fontSize="16px">
            Donor Covers Fee
          </CustomLabel>
        </LabelCell>
        <DescriptionCell>
          <FlexContainer fxLayout="column" justifyContent="center" spacing={20}>
            {this.checkBoxChange(
              "coverProcessingCost",
              this.donationStore?.pricingPageData?.coverProcessingCost || false,
              "Ask donor to cover the processing cost"
            )}
            {this.donationStore?.pricingPageData?.coverProcessingCost && (
              <div>
                {this.textInput(
                  "textarea",
                  "coverProcessingCostDescription",
                  this.donationStore?.pricingPageData
                    ?.coverProcessingCostDescription || "",
                  "",
                  "styled-input amount-range"
                )}

                <CustomLabel color="#979797" fontSize="14px" lineHeight="16px">
                  Invite donors to cover the cost to process their donation. If
                  donor agrees,
                  <br /> surcharge will apply to recurring donations too. Set in
                  Settings.
                </CustomLabel>
              </div>
            )}
          </FlexContainer>
        </DescriptionCell>
      </FlexContainer>
    );
  }

  render() {
    return (
      <CollapsiblePanel title="Advanced">
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel
              lineHeight="40px"
              fontSize="16px"
              className="standard-lh"
            >
              Donation Benefits
              <InfoIcon />
            </CustomLabel>
          </LabelCell>
          <DescriptionCell>
            <CustomLabel margin="0px 0px 20px">Beneficiaries</CustomLabel>
            <SelectCreateable
              isMulti={true}
              error={this.donationStore?.pricingPageData.hasError(
                "beneficiaries"
              )}
              options={this.options}
              selectedOptions={this.selectedBeneficiaries}
              onChange={(newValue) => this.updateSelectedBeneficiary(newValue)}
              className="donation-pricing-select"
              classPrefix="option-select"
            />
            <CustomLabel
              color="#979797"
              fontSize="14px"
              lineHeight="16px"
              margin="10px 0px 20px "
            >
              Donation will be split equally if more than one beneficiary.
              Applies to recurring donations.
            </CustomLabel>
            <FlexContainer
              fxLayout="column"
              justifyContent="center"
              spacing={20}
            >
              {this.checkBoxChange(
                "canSelectOneBeneficiary",
                this.donationStore?.pricingPageData?.canSelectOneBeneficiary ||
                  false,
                "Donor can select their beneficiary"
              )}
              {this.selectedBeneficiaries.length > 1 &&
                this.checkBoxChange(
                  "canSelectMultipleBeneficiaries",
                  this.donationStore?.pricingPageData
                    ?.canSelectMultipleBeneficiaries || false,
                  "Donor can select multiple beneficiaries"
                )}
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel lineHeight="40px" fontSize="16px">
              In Memoriam
              <InfoIcon />
            </CustomLabel>
          </LabelCell>
          <DescriptionCell>
            <FlexContainer
              fxLayout="column"
              justifyContent="center"
              spacing={20}
            >
              {this.checkBoxChange(
                "offerInMemoryOf",
                this.donationStore?.pricingPageData?.offerInMemoryOf || false,
                "Offer in honor of / in memory of option"
              )}
              {this.checkBoxChange(
                "allowComments",
                this.donationStore?.pricingPageData?.allowComments || false,
                "Allow comments"
              )}
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel lineHeight="40px" fontSize="16px">
              Annual Increase
              <InfoIcon />
            </CustomLabel>
          </LabelCell>
          <DescriptionCell>
            <FlexContainer
              fxLayout="column"
              justifyContent="center"
              spacing={20}
            >
              {this.checkBoxChange(
                "annualIncrease",
                this.donationStore?.pricingPageData?.annualIncrease || false,
                "Offer annual increase option"
              )}
              {this.donationStore?.pricingPageData?.annualIncrease && (
                <div>
                  {this.textInput(
                    "textarea",
                    "annualIncreaseDescription",
                    this.donationStore?.pricingPageData
                      ?.annualIncreaseDescription || "",
                    "",
                    "styled-input amount-range"
                  )}

                  <CustomLabel
                    color="#979797"
                    fontSize="14px"
                    lineHeight="16px"
                  >
                    Applies to recurring donations only. Donor must confirm.
                  </CustomLabel>
                </div>
              )}
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        {/* {this.coverFee} */}
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel lineHeight="40px" fontSize="16px">
              Anonymous Donations
            </CustomLabel>
          </LabelCell>
          <DescriptionCell>
            {this.checkBoxChange(
              "allowAnonymousDonation",
              this.donationStore?.pricingPageData?.allowAnonymousDonation ||
                false,
              "Allow anonymous donations"
            )}
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel lineHeight="40px" fontSize="16px">
              Donation Restrictions
              <InfoIcon />
            </CustomLabel>
          </LabelCell>
          <DescriptionCell>
            <FlexContainer
              fxLayout="column"
              justifyContent="center"
              spacing={20}
            >
              {this.checkBoxChange(
                "setLimitOnCustomDonation",
                this.donationStore?.pricingPageData?.setLimitOnCustomDonation ||
                  false,
                "Set limits on custom donation amounts"
              )}
              {this.customLimits}
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
      </CollapsiblePanel>
    );
  }
}

export default DonationAdvanceSection;
